import { useEffect, useMemo } from "react";
import { useAnnounce } from "@/app/Announcer";
import { useT } from "@/i18n/I18nProvider";

/**
 * Debug Terminal의 텍스트 대체와 스크린 리더 모드 (ADR 0025 · Issue #13)
 * --------------------------------------------------------------------------
 * 터미널 기록은 `role="log"` 영역이라 켜 두면 **출력 한 줄마다** 읽힙니다.
 * 명령 하나가 수백 줄을 뱉는 화면에서 그것은 전달이 아니라 소음입니다.
 *
 * 그래서 기록 영역의 live는 **언제나 꺼져 있고**, 낭독은 중복을 접는 공용 announcer
 * 하나가 담당합니다. 스크린 리더 모드는 "얼마나 자세히 알릴지"만 고릅니다 —
 * 꺼짐이면 단계 변화만, 켜짐이면 단계와 출력 크기까지. 어느 쪽도 원문을 읽지 않습니다.
 *
 * 요약이 담는 것은 **상태와 크기뿐**입니다. 출력 원문은 한 글자도 옮기지 않습니다 —
 * 대체 표현이 새 노출 경로가 되면 마스킹 경계가 그만큼 넓어집니다. 원문은 기존
 * 기록 영역이 그대로 갖고 있고, 사용자는 그곳으로 이동해 읽습니다.
 */

/** UTF-8 바이트를 세는 인코더. 인스턴스를 매번 만들지 않습니다. */
const ENCODER = new TextEncoder();

export interface TerminalSummary {
  lines: number;
  /** 실제 전송 바이트(UTF-8). UTF-16 코드 유닛 수가 아닙니다. */
  bytes: number;
}

/**
 * 화면 기록에서 요약을 뽑습니다. 원문을 복사해 두지 않으므로 메모리가 늘지 않고,
 * 기록 창이 유계(WINDOW)라 인코딩 비용도 유계입니다.
 */
export function summarize(text: string): TerminalSummary {
  if (text === "") return { lines: 0, bytes: 0 };
  const trimmed = text.endsWith("\n") ? text.slice(0, -1) : text;
  let lines = 1;
  for (let i = 0; i < trimmed.length; i++) if (trimmed[i] === "\n") lines++;
  return { lines, bytes: ENCODER.encode(text).length };
}

export interface DebugTerminalAltProps {
  /** 화면에 보이는 기록 그대로. 여기서 다시 요청하지 않습니다. */
  text: string;
  /** 화면에 보이는 상태 문장(남은 시간 포함). 표시 전용입니다. */
  status: string;
  /** 남은 시간이 빠진 단계 문장. 낭독은 이 값으로 합니다. */
  phase: string;
  /** 자세히 알리기(스크린 리더 모드). 켜면 출력 크기 변화까지 알립니다. */
  verbose: boolean;
}

export function DebugTerminalAlt({ text, status, phase, verbose }: DebugTerminalAltProps) {
  const { t } = useT("terminal");
  const announce = useAnnounce();
  const summary = useMemo(() => summarize(text), [text]);

  const size = useMemo(
    () => (summary.lines === 0 ? t("terminal.alt.empty") : t("terminal.alt.size", { lines: summary.lines, bytes: summary.bytes })),
    [summary, t],
  );

  /* 낭독 문장은 화면 문장과 다릅니다 — 남은 초는 빼고, 자세히 알리기가 꺼져 있으면
     단계만 말합니다. 출력 원문은 어느 쪽에도 들어가지 않습니다. */
  useEffect(() => {
    announce("debug-terminal", verbose ? `${phase} · ${size}` : phase);
  }, [announce, verbose, phase, size]);

  return (
    <p className="terminal-alt" data-testid="exec-alt" aria-label={t("terminal.alt.label")}>
      {`${status} · ${size}`}
    </p>
  );
}
